/**
 * @file AI の答えへの評価を付ける部品である。
 *
 * @what: 役に立った / 役に立たなかった → 理由 (複数可) と一言、の2段構えで、1回だけ送る。
 *   既に評価があれば文字で出す。
 * @why: 答えは書き換えないので、人が印を残せる場所は評価だけである。操作 (AI 解説 / AI 採点) が
 *   増えても同じ部品を使い、理由の語彙を揃えて横断して読めるようにする。
 * @failure: 送信中は押せない。409 (すでに評価がついている) の扱いは呼び出し側が持つ。
 */

import { useState } from "react";
import {
  AI_REACTION_REASON_LABELS,
  type AIReactionReason,
  type StudyAIReaction,
} from "../api/askRecords";

const REASONS: AIReactionReason[] = ["off_target", "wrong", "unclear", "too_long", "other"];

export function ReactionBox({
  reaction,
  subject = "この答えは",
  busy,
  onReact,
}: {
  reaction?: StudyAIReaction | undefined;
  /** 問いかけの主語 ("この答えは")。 */
  subject?: string;
  busy: boolean;
  onReact: (body: {
    helpful: boolean;
    reasons?: AIReactionReason[];
    comment?: string;
  }) => Promise<void>;
}) {
  const [step, setStep] = useState<"idle" | "reasons">("idle");
  const [reasons, setReasons] = useState<AIReactionReason[]>([]);
  const [comment, setComment] = useState("");

  if (reaction) {
    const r = reaction;
    return (
      <p className="study-ask-reaction-done">
        <span className={`study-ai-verdict study-ai-verdict--${r.helpful ? "helpful" : "not"}`}>
          {r.helpful ? "役に立った" : "役に立たなかった"}
        </span>
        {r.reasons && r.reasons.length > 0 ? (
          <span className="study-ai-verdict-note">
            {r.reasons.map((x) => AI_REACTION_REASON_LABELS[x]).join("、")}
          </span>
        ) : null}
        {r.comment ? <span className="study-ai-verdict-note">{r.comment}</span> : null}
      </p>
    );
  }
  const needComment = reasons.includes("other") && comment.trim() === "";
  return (
    <div className="study-ask-reaction" role="group" aria-label={`${subject}への評価`}>
      {step === "idle" ? (
        <>
          <span className="study-ask-reaction-q">{subject}</span>
          <button
            type="button"
            className="study-ghost-btn study-ask-vote study-ask-vote--helpful"
            disabled={busy}
            onClick={() => void onReact({ helpful: true })}
          >
            <span aria-hidden="true">◎</span> 役に立った
          </button>
          <button
            type="button"
            className="study-ghost-btn study-ask-vote study-ask-vote--not"
            disabled={busy}
            onClick={() => setStep("reasons")}
          >
            <span aria-hidden="true">△</span> 役に立たなかった
          </button>
        </>
      ) : (
        <div className="study-ask-reasons">
          <p className="study-ask-reaction-q">どこが合いませんでしたか (複数可)</p>
          {REASONS.map((r) => (
            <label key={r} className="study-ask-reason">
              <input
                type="checkbox"
                checked={reasons.includes(r)}
                onChange={(e) =>
                  setReasons((prev) =>
                    e.target.checked ? [...prev, r] : prev.filter((x) => x !== r),
                  )
                }
              />
              {AI_REACTION_REASON_LABELS[r]}
            </label>
          ))}
          <input
            type="text"
            className="study-ask-comment"
            placeholder={reasons.includes("other") ? "一言 (その他のときは必須)" : "一言 (任意)"}
            maxLength={200}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            aria-label="一言"
          />
          <p className="study-ask-dialog-actions">
            <button type="button" className="study-ghost-btn" onClick={() => setStep("idle")}>
              もどる
            </button>
            <button
              type="button"
              className="study-primary-btn"
              disabled={busy || reasons.length === 0 || needComment}
              onClick={() =>
                void onReact({
                  helpful: false,
                  reasons,
                  ...(comment.trim() ? { comment: comment.trim() } : {}),
                })
              }
            >
              送る
            </button>
          </p>
        </div>
      )}
    </div>
  );
}
